import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { H1, Loading, IconLinkButton, DeleteButton, AccessCheck } from "@components";
import { UserForm } from "features/users/components";
import { canCreateUser, canEditUser } from "features/users/utils";
import { usersService } from "features/users/services/users-service";
import { useUser } from "features/users/hooks/useUser";
import { useAuth } from "features/auth";
import { UserRoleEnum } from "features/users/types";
import type { FormAction } from "features/users/types";

export const UserPage = ({ action = "read" }: { action?: FormAction }) => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const [user, isFetching] = useUser(id, action === "create");

  if (isFetching) {
    return <Loading />;
  }

  if (!user) {
    return null;
  }

  const isCreate = action === "create";
  const isEdit = action === "edit";
  const isRead = action === "read";
  const hasEditAccess = isCreate ? canCreateUser(currentUser.role) : canEditUser(currentUser, user.role, user.id!);
  const isMe = currentUser.id === user.id;

  // eslint-disable-next-line no-nested-ternary
  const h1Label = isCreate ? "Create User" : isEdit ? "Edit User" : "User info";
  const editLink = `/users/${id}/edit`;

  return (
    <AccessCheck minAccessLevel={UserRoleEnum.Manager}>
      <H1 label={h1Label}>
        <Box sx={{ display: "flex", gap: 3 }}>
          {hasEditAccess && user.id && isRead ? <IconLinkButton to={editLink} replace /> : null}
          {hasEditAccess && user.id && !isCreate ? (
            <DeleteButton id={user.id} deleteService={usersService.deleteUser} label="User" />
          ) : null}
        </Box>
      </H1>
      <UserForm action={action} hasEditAccess={hasEditAccess} user={user} isMe={isMe} />
    </AccessCheck>
  );
};
